"use client";

import React, { useEffect, useState } from "react";
import { cn, tinaField, useTina } from "@/utils";
import CallToAction from "@/components/CallToAction";

import Page from "@/components/ui/page";
import Header from "@/components/Header";
import Section from "@/components/ui/section";
import { Button } from "@/components/ui/button";
import HighlightedText from "@/components/ui/highlighted-text";

import LogosCarousel from "@/components/LogosCarousel";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import Image from "next/image";
import { ResourceItem } from "@/components/Resources";
import TiptapSimpleContent from "@/components/TiptapSimpleContent";
import Link from "next/link";
import HomeFeaturesDesktopLazy from "@/components/HomeFeaturesDesktopLazy";
import {
  getLatestContentTabMeta,
  resolveLatestContentSources,
  sourcesMatch,
  takeLatestMatchingResources,
  latestContentSourceKey,
  type LatestContentSource,
} from "@/lib/home-latest-content";
import { fetchLatestContentTab } from "@/app/home/actions";

dayjs.extend(advancedFormat);

export default function Home(props) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const header = data?.home?.header;
  const aboutUs = data?.home?.aboutUs;
  const features = data?.home?.features;
  const callToAction = data?.home?.cta;
  const callToAction2 = data?.home?.cta2;
  const latestContent = data?.home?.latestContent;

  useEffect(() => {
    if (window.location.pathname === "/") {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <Page
      defaultTinaField={data?.home}
      seo={data?.home?.seo}
      pagePath="/"
      titleFallback="Binarly"
    >
      <Header
        {...header}
        className="sticky top-20 pb-32"
        variant="center"
        showBreadcrumbs={false}
        backgroundVideo={header?.video}
        backgroundPoster={header?.image}
        backgroundVariant="pixels-bl-tr"
        backgroundVideoClassName="object-cover object-right-bottom"
      />
      <LogosCarousel backgroundOnScroll={true} />
      <div className="relative bg-background z-10 [&>section]:py-12 lg:[&>section]:py-24">
        <AboutUs aboutUs={aboutUs} />
        <Features features={features} />
        <CallToAction
          {...callToAction}
          variant="center"
          backgroundVariant="pixels-subtle"
        />
        <LatestContent
          latestContent={latestContent}
          latestContentQueries={props.latestContentQueries ?? []}
        />
        <CallToAction
          {...callToAction2}
          variant="left"
          backgroundVariant="dots"
          wrapperClassName="!py-16 lg:!py-32"
        />
      </div>
    </Page>
  );
}

function AboutUs({ aboutUs }: { aboutUs: any }) {
  return (
    <Section className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-36">
      <div className="flex flex-col justify-between gap-4 lg:gap-16">
        <h2 className="h4" data-tina-field={tinaField(aboutUs, "title")}>
          {aboutUs?.title}
        </h2>
        <TiptapSimpleContent
          content={aboutUs?.description}
          className="p"
          data-tina-field={tinaField(aboutUs, "description")}
        />
      </div>
      <div className="flex flex-col items-start gap-10">
        <HighlightedText
          as="h2"
          className="h3 text-muted-foreground-lighter text-balance [&_br]:hidden"
          highlightClassName="text-white"
          text={aboutUs?.tagline}
          data-tina-field={tinaField(aboutUs, "tagline")}
        />

        {aboutUs?.button?.label && (
          <Button
            variant="outline"
            size="lg"
            href={aboutUs?.button?.link}
            aria-label="Explore Binarly platform features"
            data-tina-field={tinaField(aboutUs, "button")}
          >
            {aboutUs?.button?.label}
            <span className="sr-only"> — {aboutUs?.title}</span>
          </Button>
        )}
      </div>
    </Section>
  );
}

function Features({ features }: { features: any }) {
  if (!features?.features || features.features.length === 0) {
    return null;
  }

  return (
    <Section
      wrapperClassName="!pt-8 lg:!pb-32"
      className="flex flex-col gap-12"
    >
      {/* Mobile layout - stacked cards. Server-rendered. */}
      <div className="flex flex-col gap-6 lg:hidden">
        {features.features.map((feature: any, index: number) => (
          <div
            key={index}
            className="bg-background-light border !border-primary-200 overflow-hidden relative"
          >
            <div className="relative w-full p-6 flex flex-col z-20">
              <div className="w-full mb-6 aspect-video overflow-hidden">
                <Image
                  src={feature.image}
                  alt={feature.title}
                  width={500}
                  height={300}
                  sizes="(max-width: 1024px) 100vw, 500px"
                  className="w-full h-full object-contain"
                  data-tina-field={tinaField(feature, "image")}
                />
              </div>

              <div>
                <span className="font-mono text-sm text-primary-200 mb-3 block">
                  {String(index + 1).padStart(2, "0")}
                </span>

                <h3
                  className="text-2xl font-medium text-white text-balance mb-3"
                  data-tina-field={tinaField(feature, "title")}
                >
                  {feature.title}
                </h3>

                <TiptapSimpleContent
                  content={feature.description}
                  className="text-muted-foreground"
                  data-tina-field={tinaField(feature, "description")}
                />
              </div>
            </div>
            {feature.link && (
              <Link
                href={feature.link}
                className="absolute inset-0 z-30 rounded-none outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                aria-label={`Feature ${index + 1}: ${feature.title}`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Desktop layout - dynamic import, gated on `(min-width: 1024px)`.
          Mobile visitors do not download motion `AnimatePresence`/`popLayout`,
          `PixelEffect`, or the related runtime. */}
      <HomeFeaturesDesktopLazy features={features} />
    </Section>
  );
}

function LatestContent({
  latestContent,
  latestContentQueries,
}: {
  latestContent: any;
  latestContentQueries: {
    resourceType: LatestContentSource["resourceType"];
    categories: string[];
    query: any;
  }[];
}) {
  const sources = resolveLatestContentSources(latestContent?.sources);
  const tabs = sources.map((source) => {
    const ssrMatch = latestContentQueries.find((entry) =>
      sourcesMatch(entry, source)
    );
    return {
      source,
      ...getLatestContentTabMeta(source),
      query: ssrMatch?.query,
    };
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const activeIndex = selectedIndex < tabs.length ? selectedIndex : 0;
  const selectedTab = tabs[activeIndex];

  if (tabs.length === 0) {
    return null;
  }

  return (
    <Section className="flex flex-col gap-6 lg:gap-10">
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between">
        <h2 className="h3" data-tina-field={tinaField(latestContent, "title")}>
          {latestContent?.title}
        </h2>

        <Button
          variant="link-glow"
          className="px-0 text-base"
          size="lg"
          href={selectedTab?.allLink || "/"}
        >
          {selectedTab?.allLabel}
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-8 lg:gap-16">
        <div role="radiogroup" aria-label="Latest content type">
          {tabs.map((tab, index) => {
            const selected = activeIndex === index;
            return (
              <button
                key={`${tab.source.resourceType}-${index}`}
                type="button"
                role="radio"
                aria-checked={selected}
                className={cn(
                  "flex w-full items-center gap-2 border-b py-4 text-left text-muted-foreground-light hover:text-white transition-[color,box-shadow] rounded-none outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                  selected &&
                    "text-white border-secondary-400 glow-border-b glow-secondary"
                )}
                onClick={() => setSelectedIndex(index)}
              >
                <span className="font-mono" aria-hidden>
                  {String(index + 1).padStart(2, "0")}
                </span>
                {tab.label}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {selectedTab?.query ? (
            <LatestContentTabPanel
              key={latestContentSourceKey(selectedTab.source)}
              query={selectedTab.query}
              categories={selectedTab.source.categories}
            />
          ) : (
            <LatestContentLivePanel
              key={latestContentSourceKey(selectedTab.source)}
              source={selectedTab.source}
            />
          )}
        </div>
      </div>
    </Section>
  );
}

function LatestContentTabPanel({
  query,
  categories,
}: {
  query: any;
  categories: string[];
}) {
  const { data } = useTina(query);
  const resources = takeLatestMatchingResources(
    (data as any)?.resourcesConnection?.edges?.map((edge) => edge?.node),
    categories
  );

  return resources.map((resource: any) => (
    <ResourceItem key={resource.id} resource={resource} viewMode="grid" />
  ));
}

function LatestContentLivePanel({ source }: { source: LatestContentSource }) {
  const [resources, setResources] = useState<any[] | null>(null);
  const cacheKey = latestContentSourceKey(source);

  useEffect(() => {
    let cancelled = false;
    setResources(null);

    fetchLatestContentTab({
      resourceType: source.resourceType,
      categories: source.categories,
    })
      .then((nodes) => {
        if (!cancelled) {
          setResources(nodes);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResources([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey]);

  if (!resources) {
    return null;
  }

  return resources.map((resource: any) => (
    <ResourceItem key={resource.id} resource={resource} viewMode="grid" />
  ));
}
